import { useCallback, useEffect, useState } from 'react';
import { Globe, Download, CheckCircle2, RefreshCw, AlertCircle, X, ChevronRight, Code2, LifeBuoy, Monitor, Home } from 'lucide-react';
import JSZip from 'jszip';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { SupportTicketModal } from './aandeslag/SupportTicketModal';
import pluginSource from '../../wordpress-plugin/dibbi-blog-sync/dibbi-blog-sync.php?raw';
import uninstallSource from '../../wordpress-plugin/dibbi-blog-sync/uninstall.php?raw';

interface WpConnectionInfo {
  widget_api_key: string | null;
  widget_enabled: boolean | null;
  wp_site_url: string | null;
  wp_last_sync_at: string | null;
  custom_site_active: boolean | null;
  custom_site_url: string | null;
  joepa_active: boolean | null;
  joepa_site_url: string | null;
}

type KoppelStatus = 'verbonden' | 'geen_contact' | 'niet_gekoppeld';

/**
 * De plugin meldt zich elk kwartier. Langer dan een uur niets gehoord
 * betekent dat de plugin uitstaat of de website onbereikbaar is.
 */
function computeStatus(info: WpConnectionInfo | null): KoppelStatus {
  if (!info?.wp_site_url || !info.wp_last_sync_at) return 'niet_gekoppeld';
  const minuten = (Date.now() - new Date(info.wp_last_sync_at).getTime()) / 60000;
  return minuten <= 60 ? 'verbonden' : 'geen_contact';
}

const STATUS_CONFIG: Record<KoppelStatus, { label: string; dot: string; text: string }> = {
  verbonden: { label: 'Gekoppeld', dot: 'bg-emerald-500', text: 'text-emerald-700' },
  geen_contact: { label: 'Geen contact', dot: 'bg-amber-500', text: 'text-amber-700' },
  niet_gekoppeld: { label: 'Niet gekoppeld', dot: 'bg-gray-300', text: 'text-gray-500' },
};

export function KoppelingenSection() {
  const { user } = useAuth();
  const [info, setInfo] = useState<WpConnectionInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [apiModalOpen, setApiModalOpen] = useState(false);
  const [customSiteModalOpen, setCustomSiteModalOpen] = useState(false);
  const [joepaModalOpen, setJoepaModalOpen] = useState(false);
  const [supportOpen, setSupportOpen] = useState(false);
  const [supportSubject, setSupportSubject] = useState('API-koppeling');

  const loadInfo = useCallback(async () => {
    if (!user?.id) return;
    setLoading(true);
    const { data } = await supabase
      .from('users')
      .select('widget_api_key, widget_enabled, wp_site_url, wp_last_sync_at, custom_site_active, custom_site_url, joepa_active, joepa_site_url')
      .eq('id', user.id)
      .single();
    setInfo(data ?? null);
    setLoading(false);
  }, [user?.id]);

  useEffect(() => {
    loadInfo();
  }, [loadInfo]);

  const status = computeStatus(info);
  const statusConfig = STATUS_CONFIG[status];
  const customSiteActive = !!info?.custom_site_active;
  const joepaActive = !!info?.joepa_active;

  return (
    <div className="bg-white rounded-2xl border border-gray-200 p-6">
      <h2 className="text-lg font-bold text-[#231F20] mb-1">Koppelingen</h2>
      <p className="text-sm text-gray-600 mb-5">
        Verbind dibbi met je eigen website, zodat je artikelen ook daar verschijnen.
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <button
          onClick={() => setModalOpen(true)}
          className="text-left bg-white border border-gray-200 rounded-xl p-5 hover:border-[#FF6B35] hover:shadow-sm transition-all group"
        >
          <div className="flex items-start justify-between mb-3">
            <div className="w-10 h-10 rounded-lg bg-[#21759b]/10 flex items-center justify-center">
              <Globe className="w-5 h-5 text-[#21759b]" />
            </div>
            <span className="inline-flex items-center px-2 py-0.5 bg-purple-100 text-purple-700 text-xs font-medium rounded-full">
              Beta
            </span>
          </div>
          <h3 className="font-semibold text-[#231F20] mb-1 flex items-center gap-1">
            WordPress
            <ChevronRight className="w-4 h-4 text-gray-400 group-hover:text-[#FF6B35] transition-colors" />
          </h3>
          <p className="text-sm text-gray-600 mb-3">
            Je blogartikelen automatisch als berichten op je eigen WordPress-website.
          </p>
          <div className="flex items-center gap-2">
            <span className={`w-2 h-2 rounded-full ${loading ? 'bg-gray-300 animate-pulse' : statusConfig.dot}`} />
            <span className={`text-xs font-medium ${statusConfig.text}`}>
              {loading ? 'Laden...' : statusConfig.label}
            </span>
          </div>
        </button>

        <button
          onClick={() => setCustomSiteModalOpen(true)}
          className="text-left bg-white border border-gray-200 rounded-xl p-5 hover:border-[#FF6B35] hover:shadow-sm transition-all group"
        >
          <div className="flex items-start justify-between mb-3">
            <div className="w-10 h-10 rounded-lg bg-[#FF6B35]/10 flex items-center justify-center">
              <Monitor className="w-5 h-5 text-[#FF6B35]" />
            </div>
            <span className="inline-flex items-center px-2 py-0.5 bg-orange-100 text-orange-700 text-xs font-medium rounded-full">
              Op maat
            </span>
          </div>
          <h3 className="font-semibold text-[#231F20] mb-1 flex items-center gap-1">
            Eigen website
            <ChevronRight className="w-4 h-4 text-gray-400 group-hover:text-[#FF6B35] transition-colors" />
          </h3>
          <p className="text-sm text-gray-600 mb-3">
            Geen WordPress? Onze plugin werkt op elke website. We helpen je graag om hem werkend te krijgen.
          </p>
          <div className="flex items-center gap-2">
            <span className={`w-2 h-2 rounded-full ${loading ? 'bg-gray-300 animate-pulse' : customSiteActive ? 'bg-emerald-500' : 'bg-gray-300'}`} />
            <span className={`text-xs font-medium ${loading ? 'text-gray-500' : customSiteActive ? 'text-emerald-700' : 'text-gray-500'}`}>
              {loading ? 'Laden...' : customSiteActive ? 'Actief' : 'Niet gekoppeld'}
            </span>
          </div>
        </button>

        {joepaActive && (
        <button
          onClick={() => setJoepaModalOpen(true)}
          className="text-left bg-white border border-gray-200 rounded-xl p-5 hover:border-[#FF6B35] hover:shadow-sm transition-all group"
        >
          <div className="flex items-start justify-between mb-3">
            <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
              <Home className="w-5 h-5 text-amber-600" />
            </div>
            <span className="inline-flex items-center px-2 py-0.5 bg-amber-100 text-amber-700 text-xs font-medium rounded-full">
              Website + blog
            </span>
          </div>
          <h3 className="font-semibold text-[#231F20] mb-1 flex items-center gap-1">
            joepa-website
            <ChevronRight className="w-4 h-4 text-gray-400 group-hover:text-[#FF6B35] transition-colors" />
          </h3>
          <p className="text-sm text-gray-600 mb-3">
            Nog geen website? joepa bouwt hem voor je, met je dibbi-blogs er automatisch op.
          </p>
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-emerald-500" />
            <span className="text-xs font-medium text-emerald-700">Actief</span>
          </div>
        </button>
        )}

        <button
          onClick={() => setApiModalOpen(true)}
          className="text-left bg-white border border-gray-200 rounded-xl p-5 hover:border-[#FF6B35] hover:shadow-sm transition-all group"
        >
          <div className="flex items-start justify-between mb-3">
            <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center">
              <Code2 className="w-5 h-5 text-slate-600" />
            </div>
            <span className="inline-flex items-center px-2 py-0.5 bg-slate-100 text-slate-600 text-xs font-medium rounded-full">
              Technisch
            </span>
          </div>
          <h3 className="font-semibold text-[#231F20] mb-1 flex items-center gap-1">
            API
            <ChevronRight className="w-4 h-4 text-gray-400 group-hover:text-[#FF6B35] transition-colors" />
          </h3>
          <p className="text-sm text-gray-600 mb-3">
            Koppel dibbi via onze API aan je eigen systeem of website. Voor maatwerk en andere CMS'en dan WordPress.
          </p>
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-slate-300" />
            <span className="text-xs font-medium text-gray-500">Vereist technische kennis</span>
          </div>
        </button>
      </div>

      {modalOpen && (
        <WordPressModal
          info={info}
          status={status}
          onRefresh={loadInfo}
          loading={loading}
          onClose={() => setModalOpen(false)}
        />
      )}

      {apiModalOpen && (
        <ApiModal
          onClose={() => setApiModalOpen(false)}
          onContactSupport={() => {
            setApiModalOpen(false);
            setSupportSubject('API-koppeling');
            setSupportOpen(true);
          }}
        />
      )}

      {customSiteModalOpen && (
        <CustomSiteModal
          active={customSiteActive}
          siteUrl={info?.custom_site_url ?? null}
          onClose={() => setCustomSiteModalOpen(false)}
          onContactSupport={() => {
            setCustomSiteModalOpen(false);
            setSupportSubject('Website-koppeling');
            setSupportOpen(true);
          }}
        />
      )}

      {joepaModalOpen && (
        <JoepaModal
          active={joepaActive}
          siteUrl={info?.joepa_site_url ?? null}
          onClose={() => setJoepaModalOpen(false)}
          onContactSupport={() => {
            setJoepaModalOpen(false);
            setSupportSubject('joepa-website');
            setSupportOpen(true);
          }}
        />
      )}

      {supportOpen && (
        <SupportTicketModal
          initialSubject={supportSubject}
          onClose={() => setSupportOpen(false)}
        />
      )}
    </div>
  );
}

interface ApiModalProps {
  onClose: () => void;
  onContactSupport: () => void;
}

function ApiModal({ onClose, onContactSupport }: ApiModalProps) {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto p-6 relative">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          title="Sluiten"
        >
          <X className="w-5 h-5" />
        </button>

        <h2 className="text-lg font-bold text-[#231F20] mb-1 flex items-center gap-2 pr-10">
          <Code2 className="w-5 h-5" />
          API-koppeling
          <span className="inline-flex items-center px-2 py-0.5 bg-slate-100 text-slate-600 text-xs font-medium rounded-full">
            Technisch
          </span>
        </h2>
        <p className="text-sm text-gray-600 mb-4">
          Met de dibbi-API koppel je dibbi rechtstreeks aan je eigen systeem of website. Elke blog die je
          in dibbi publiceert kan dan automatisch op je eigen site verschijnen. Handig voor maatwerk of
          een ander CMS dan WordPress.
        </p>

        <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 mb-5">
          <p className="text-sm text-gray-700">
            Dit is een technische koppeling die wat ontwikkelkennis vraagt (jij of je webbouwer). We richten
            hem daarom graag samen met je in, zodat het in een keer goed staat. Stuur ons een berichtje, dan
            denken we met je mee over de mogelijkheden.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <button onClick={onContactSupport} className="btn-primary flex items-center gap-2">
            <LifeBuoy className="w-5 h-5" />
            Neem contact op met support
          </button>
          <button onClick={onClose} className="btn-secondary">
            Sluiten
          </button>
        </div>
      </div>
    </div>
  );
}

interface CustomSiteModalProps {
  active: boolean;
  siteUrl: string | null;
  onClose: () => void;
  onContactSupport: () => void;
}

function CustomSiteModal({ active, siteUrl, onClose, onContactSupport }: CustomSiteModalProps) {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto p-6 relative">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          title="Sluiten"
        >
          <X className="w-5 h-5" />
        </button>

        <h2 className="text-lg font-bold text-[#231F20] mb-1 flex items-center gap-2 pr-10">
          <Monitor className="w-5 h-5" />
          Eigen website koppelen
          <span className="inline-flex items-center px-2 py-0.5 bg-orange-100 text-orange-700 text-xs font-medium rounded-full">
            Op maat
          </span>
        </h2>
        <p className="text-sm text-gray-600 mb-4">
          Draait je website niet op WordPress? Geen probleem. We hebben een plugin die op elke
          website werkt, zodat elke blog die je publiceert vanzelf op je eigen site verschijnt.
          We helpen graag om dit werkend te krijgen, in overleg met jouw websitebouwer.
        </p>

        {active ? (
          <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 flex items-start gap-3 mb-5">
            <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
            <div className="min-w-0">
              <p className="text-sm font-medium text-emerald-900">
                {siteUrl ? (
                  <>
                    Gekoppeld met{' '}
                    <a
                      href={siteUrl.startsWith('http') ? siteUrl : `https://${siteUrl}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="underline break-all"
                    >
                      {siteUrl.replace(/^https?:\/\//, '')}
                    </a>
                  </>
                ) : (
                  'Je website is gekoppeld'
                )}
              </p>
              <p className="text-sm text-emerald-800">
                Je blogs verschijnen automatisch op je eigen website. Vragen of wijzigingen?
                Stuur ons gerust een berichtje.
              </p>
            </div>
          </div>
        ) : (
          <div className="bg-orange-50 border border-orange-200 rounded-xl p-4 mb-5">
            <p className="text-sm text-gray-700">
              Zo werkt het: stuur ons een berichtje via de knop hieronder. We nemen contact met je
              op en stemmen samen met jou of je websitebouwer af hoe we de plugin op jouw site
              werkend krijgen. Zodra alles staat, zie je hier &lsquo;Actief&rsquo; verschijnen.
            </p>
          </div>
        )}

        <div className="flex flex-wrap items-center gap-3">
          <button onClick={onContactSupport} className="btn-primary flex items-center gap-2">
            <LifeBuoy className="w-5 h-5" />
            Neem contact op met support
          </button>
          <button onClick={onClose} className="btn-secondary">
            Sluiten
          </button>
        </div>
      </div>
    </div>
  );
}

interface JoepaModalProps {
  active: boolean;
  siteUrl: string | null;
  onClose: () => void;
  onContactSupport: () => void;
}

function JoepaModal({ active, siteUrl, onClose, onContactSupport }: JoepaModalProps) {
  const blogUrl = siteUrl
    ? `${(siteUrl.startsWith('http') ? siteUrl : `https://${siteUrl}`).replace(/\/+$/, '')}/blog`
    : null;
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto p-6 relative">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          title="Sluiten"
        >
          <X className="w-5 h-5" />
        </button>

        <h2 className="text-lg font-bold text-[#231F20] mb-1 flex items-center gap-2 pr-10">
          <Home className="w-5 h-5" />
          joepa-website
          <span className="inline-flex items-center px-2 py-0.5 bg-amber-100 text-amber-700 text-xs font-medium rounded-full">
            Website + blog
          </span>
        </h2>
        <p className="text-sm text-gray-600 mb-4">
          joepa bouwt complete websites voor ondernemers. Heb je een joepa-website, dan verschijnen
          je dibbi-blogs daar automatisch als volwaardige pagina&apos;s op je eigen domein: mooi in
          je huisstijl en goed vindbaar in Google. Meer weten? Kijk op{' '}
          <a
            href="https://www.joepa.nl"
            target="_blank"
            rel="noopener noreferrer"
            className="text-[#FF6B35] underline hover:no-underline"
          >
            www.joepa.nl
          </a>
          .
        </p>

        {active ? (
          <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 flex items-start gap-3 mb-5">
            <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
            <div className="min-w-0">
              <p className="text-sm font-medium text-emerald-900">
                {blogUrl ? (
                  <>
                    Gekoppeld: je blogs staan op{' '}
                    <a
                      href={blogUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="underline break-all"
                    >
                      {blogUrl.replace(/^https?:\/\//, '')}
                    </a>
                  </>
                ) : (
                  'Je joepa-website is gekoppeld'
                )}
              </p>
              <p className="text-sm text-emerald-800">
                Elke blog die je publiceert of aanpast staat binnen een minuut ook op je website.
              </p>
            </div>
          </div>
        ) : (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-5">
            <p className="text-sm text-gray-700">
              Interesse in een eigen website met je blogs erop? Stuur ons een berichtje, dan
              vertellen we je wat joepa voor je kan bouwen. Zodra je website live is, zie je
              hier &lsquo;Actief&rsquo; verschijnen.
            </p>
          </div>
        )}

        <div className="flex flex-wrap items-center gap-3">
          <button onClick={onContactSupport} className="btn-primary flex items-center gap-2">
            <LifeBuoy className="w-5 h-5" />
            Neem contact op
          </button>
          <button onClick={onClose} className="btn-secondary">
            Sluiten
          </button>
        </div>
      </div>
    </div>
  );
}

interface WordPressModalProps {
  info: WpConnectionInfo | null;
  status: KoppelStatus;
  loading: boolean;
  onRefresh: () => void;
  onClose: () => void;
}

function WordPressModal({ info, status, loading, onRefresh, onClose }: WordPressModalProps) {
  const { user } = useAuth();
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleDownload = async () => {
    if (!info?.widget_api_key || !user?.id) {
      setError('Er is nog geen API-sleutel voor je account. Neem contact op met support.');
      return;
    }
    setDownloading(true);
    setError(null);

    try {
      if (!info.widget_enabled) {
        await supabase
          .from('users')
          .update({ widget_enabled: true })
          .eq('id', user.id);
      }

      const zip = new JSZip();
      zip.file('dibbi-blog-sync/dibbi-blog-sync.php', pluginSource);
      zip.file('dibbi-blog-sync/uninstall.php', uninstallSource);
      zip.file('dibbi-blog-sync/dibbi-key.php', `<?php\nreturn '${info.widget_api_key}';\n`);

      const blob = await zip.generateAsync({ type: 'blob' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'dibbi-blog-sync.zip';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch {
      setError('Het zip-bestand kon niet gemaakt worden. Probeer het opnieuw.');
    } finally {
      setDownloading(false);
    }
  };

  const lastSync = info?.wp_last_sync_at
    ? new Date(info.wp_last_sync_at).toLocaleString('nl-NL', { dateStyle: 'medium', timeStyle: 'short' })
    : null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6 relative">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          title="Sluiten"
        >
          <X className="w-5 h-5" />
        </button>

        <h2 className="text-lg font-bold text-[#231F20] mb-1 flex items-center gap-2 pr-10">
          <Globe className="w-5 h-5" />
          WordPress-koppeling
          <span className="inline-flex items-center px-2 py-0.5 bg-purple-100 text-purple-700 text-xs font-medium rounded-full">
            Beta
          </span>
        </h2>
        <p className="text-sm text-gray-600 mb-4">
          Heb je een WordPress-website? Met de dibbi-plugin verschijnen je blogartikelen vanzelf op
          je eigen site. Pas je een artikel in dibbi aan, dan wordt het daar ook bijgewerkt.
        </p>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-3 mb-4">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}

        {!loading && status === 'verbonden' && info?.wp_site_url && (
          <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 flex items-start gap-3 mb-4">
            <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
            <div className="min-w-0">
              <p className="text-sm font-medium text-emerald-900">
                Gekoppeld met{' '}
                <a
                  href={info.wp_site_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline break-all"
                >
                  {info.wp_site_url.replace(/^https?:\/\//, '')}
                </a>
              </p>
              {lastSync && (
                <p className="text-sm text-emerald-800">Laatste synchronisatie: {lastSync}</p>
              )}
            </div>
          </div>
        )}

        {!loading && status === 'geen_contact' && info?.wp_site_url && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3 mb-4">
            <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="min-w-0">
              <p className="text-sm font-medium text-amber-900">
                Geen contact meer met {info.wp_site_url.replace(/^https?:\/\//, '')}
              </p>
              <p className="text-sm text-amber-800">
                {lastSync && <>Laatste contact: {lastSync}. </>}
                Controleer of je website bereikbaar is en of de dibbi-plugin nog aanstaat.
              </p>
            </div>
          </div>
        )}

        {!loading && status === 'niet_gekoppeld' && (
          <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 mb-4">
            <p className="text-sm text-gray-700">
              Nog niet gekoppeld. Download de plugin en installeer hem op je WordPress-website.
            </p>
          </div>
        )}

        <ol className="text-sm text-gray-700 space-y-2 mb-5 list-decimal list-inside">
          <li>Download hieronder de plugin. Je persoonlijke sleutel zit al in het bestand.</li>
          <li>
            Log in op je WordPress-website en ga naar <strong>Plugins</strong>,{' '}
            <strong>Nieuwe plugin</strong>, <strong>Plugin uploaden</strong>. Kies het gedownloade
            zip-bestand en klik daarna op <strong>Activeren</strong>.
          </li>
          <li>
            Klaar. Je website haalt vanaf nu elk kwartier je nieuwste artikelen op. In WordPress
            kun je bij Instellingen, dibbi kiezen of artikelen direct gepubliceerd worden of eerst
            als concept klaarstaan.
          </li>
        </ol>

        <div className="flex flex-wrap items-center gap-3 mb-4">
          <button
            onClick={handleDownload}
            disabled={downloading || loading}
            className="btn-primary flex items-center gap-2"
          >
            {downloading ? (
              <>
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Bezig...
              </>
            ) : (
              <>
                <Download className="w-5 h-5" />
                Download de plugin (zip)
              </>
            )}
          </button>
          <button
            onClick={onRefresh}
            disabled={loading}
            className="btn-secondary flex items-center gap-2"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Status vernieuwen
          </button>
        </div>

        <p className="text-xs text-gray-500 border-t border-gray-100 pt-4">
          Deze koppeling is nieuw en nog in beta. Werkt er iets niet zoals verwacht? Laat het ons
          weten via de supportknop, dan kijken we direct mee.
        </p>
      </div>
    </div>
  );
}
